import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import {
  FaCar, FaSearch, FaPlus, FaEdit, FaTimes, FaSave, FaUpload, FaCheckCircle, FaTimesCircle,
  FaSpinner, FaExclamationTriangle, FaChevronLeft, FaChevronRight, FaStop, FaHourglassHalf
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import ImportarDocumentoVeiculo from '../components/ImportarDocumentoVeiculo';
import { lerDocumentoVeiculo } from '../others/ocrDocumento';
import { buscarVeiculo, salvarVeiculo } from '../others/cadastroVeiculo';
import {
  PageBackground, Content, TitleRow, Title, Card, CardHeader, CardTitle, FormRow, InputGroup, Label, HintText,
  ErrorText, Input, EmptyState, PrimaryButton, SecondaryButton, ModalOverlay, ModalBox, ModalHeader, ModalClose,
  ModalContent, SearchRow
} from '../others/documentosBase';

// Tela de Veículos: consulta e edição do cadastro de veículos usado nos requerimentos e declarações,
// e importação de vários CRLV-e/ATPV/CRV de uma vez (lidos um por um, no navegador; nenhum arquivo é enviado
// nem guardado no sistema, só os dados lidos).

const TAMANHO_PAGINA = 20;
const TIPOS_ACEITOS = ['image/jpeg', 'image/png', 'application/pdf'];
const TAMANHO_MAXIMO_LEITURA_MB = 10;
const AVISO_LEITURA = 'Verifique os dados importados, pois podem ocorrer eventuais falhas de leitura.';

const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const formatarPlaca = (p) => {
  const s = (p || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return s.length === 7 ? `${s.slice(0, 3)}-${s.slice(3)}` : s;
};
const placaValida = (p) => /^[A-Z]{3}\d[A-Z0-9]\d{2}$/.test((p || '').toUpperCase().replace(/[^A-Z0-9]/g, ''));
const formatarData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '');

const formVazio = () => ({ idVeiculo: null, placa: '', chassi: '', renavam: '', motor: '', marcaModelo: '', anoFabricacaoModelo: '', cor: '' });

function Veiculos() {
  const { API_BASE_URL } = config;
  const { addToast: addToastContexto } = useToast();
  // addToast do contexto muda a cada render: a ref evita recarregar a lista em loop a cada aviso
  const addToastRef = useRef(addToastContexto);
  useEffect(() => { addToastRef.current = addToastContexto; }, [addToastContexto]);
  const addToast = useCallback((tipo, mensagem) => addToastRef.current(tipo, mensagem), []);

  // ── Lista ────────────────────────────────────────────────────────────────
  const [veiculos, setVeiculos] = useState([]);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [pagina, setPagina] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [totalElementos, setTotalElementos] = useState(0);
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(async (paginaDesejada, termo) => {
    setCarregando(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/veiculos`, {
        ...getAuthHeader(),
        params: { busca: termo, page: paginaDesejada, size: TAMANHO_PAGINA },
      });
      setVeiculos(data.content || []);
      setTotalPaginas(data.totalPages || 0);
      setTotalElementos(data.totalElements || 0);
      setPagina(paginaDesejada);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
      addToast('error', 'Erro ao carregar os veículos.');
    } finally {
      setCarregando(false);
    }
  }, [API_BASE_URL, addToast]);

  useEffect(() => { carregar(0, ''); }, [carregar]);

  const pesquisar = () => {
    setBuscaAplicada(busca.trim());
    carregar(0, busca.trim());
  };

  // ── Cadastro / edição ────────────────────────────────────────────────────
  const [modalAberto, setModalAberto] = useState(false);
  const [form, setForm] = useState(formVazio());
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);

  const abrirNovo = () => {
    setForm(formVazio());
    setErros({});
    setModalAberto(true);
  };

  const abrirEdicao = (v) => {
    setForm({
      idVeiculo: v.idVeiculo,
      placa: formatarPlaca(v.placa),
      chassi: v.chassi || '',
      renavam: v.renavam || '',
      motor: v.motor || '',
      marcaModelo: v.marcaModelo || '',
      anoFabricacaoModelo: v.anoFabricacaoModelo || '',
      cor: v.cor || '',
    });
    setErros({});
    setModalAberto(true);
  };

  const campo = (nome, transformar = (x) => x) => (ev) => {
    const valor = transformar(ev.target.value);
    setForm((prev) => ({ ...prev, [nome]: valor }));
    setErros((prev) => ({ ...prev, [nome]: undefined, geral: undefined }));
  };

  // Documento lido no formulário: preenche os campos (só grava ao clicar em Salvar)
  const aplicarDocumento = (dados) => {
    setForm((prev) => {
      const novo = { ...prev };
      Object.keys(dados).forEach((c) => { if (dados[c]) novo[c] = c === 'placa' ? formatarPlaca(dados[c]) : dados[c]; });
      return novo;
    });
    setErros({});
  };

  const validarForm = () => {
    const e = {};
    const temPlaca = form.placa.trim() !== '';
    const temChassi = form.chassi.trim() !== '';
    if (!temPlaca && !temChassi) e.geral = 'Informe ao menos a placa ou o chassi.';
    if (temPlaca && !placaValida(form.placa)) e.placa = 'Placa inválida (formato AAA0A00 ou AAA-0000).';
    if (temChassi && (form.chassi.length < 5 || form.chassi.length > 20)) e.chassi = 'Chassi deve ter de 5 a 20 caracteres (o padrão é 17).';
    const renavam = form.renavam.replace(/\D/g, '');
    if (renavam && (renavam.length < 9 || renavam.length > 11)) e.renavam = 'RENAVAM deve ter de 9 a 11 dígitos.';
    setErros(e);
    return Object.keys(e).length === 0;
  };

  const salvar = async () => {
    if (!validarForm()) return;
    setSalvando(true);
    const payload = {
      placa: form.placa.replace(/[^A-Z0-9]/gi, '').toUpperCase(),
      chassi: form.chassi,
      renavam: form.renavam.replace(/\D/g, ''),
      motor: form.motor,
      marcaModelo: form.marcaModelo,
      anoFabricacaoModelo: form.anoFabricacaoModelo,
      cor: form.cor,
    };
    try {
      if (form.idVeiculo) {
        await axios.put(`${API_BASE_URL}/veiculos/${form.idVeiculo}`, payload, getAuthHeader());
        addToast('success', 'Veículo atualizado com sucesso!');
      } else {
        // Se a placa/chassi já existir, o cadastro existente é completado (não duplica)
        const existente = await buscarVeiculo(API_BASE_URL, { placa: payload.placa, chassi: payload.chassi });
        await axios.post(`${API_BASE_URL}/veiculos`, payload, getAuthHeader());
        addToast('success', existente ? 'Veículo já existia: cadastro atualizado com os dados informados.' : 'Veículo cadastrado com sucesso!');
      }
      setModalAberto(false);
      carregar(form.idVeiculo ? pagina : 0, buscaAplicada);
    } catch (error) {
      console.error('Erro ao salvar veículo:', error);
      setErros({ geral: error.response?.data?.message || 'Erro ao salvar o veículo.' });
    } finally {
      setSalvando(false);
    }
  };

  // ── Importação em massa ──────────────────────────────────────────────────
  // Fila de arquivos lidos um por um: termina a leitura e o cadastro de um e parte para o próximo.
  const [fila, setFila] = useState([]); // [{ id, nome, status, progresso, mensagem, placa, marcaModelo }]
  const [processando, setProcessando] = useState(false);
  const cancelarRef = useRef(false);
  const inputMassaRef = useRef(null);

  const atualizarItem = (id, mudancas) => setFila((prev) => prev.map((i) => (i.id === id ? { ...i, ...mudancas } : i)));

  const processarFila = async (itens) => {
    setProcessando(true);
    cancelarRef.current = false;
    let cadastrados = 0;
    let atualizados = 0;
    let falhas = 0;

    for (const item of itens) {
      if (cancelarRef.current) {
        atualizarItem(item.id, { status: 'cancelado', mensagem: 'Cancelado' });
        continue;
      }
      if (item.status === 'erro') { falhas++; continue; } // já recusado (formato/tamanho)

      atualizarItem(item.id, { status: 'lendo', progresso: 0, mensagem: 'Iniciando...' });
      try {
        const { dados: lido, duvidas } = await lerDocumentoVeiculo(item.arquivo, (percentual, mensagem) => {
          atualizarItem(item.id, { progresso: percentual, mensagem });
        });
        if (!lido.placa && !lido.chassi) {
          falhas++;
          atualizarItem(item.id, { status: 'erro', mensagem: 'Não consegui ler a placa nem o chassi. Cadastre este manualmente.' });
          continue;
        }
        atualizarItem(item.id, { status: 'gravando', mensagem: 'Gravando no cadastro...' });
        const dados = {
          placa: lido.placa || '', chassi: lido.chassi || '', renavam: lido.renavam || '', motor: lido.motor || '',
          marcaModelo: lido.marcaModelo || '', anoFabricacaoModelo: lido.ano || '', cor: lido.cor || '',
        };
        const existente = await buscarVeiculo(API_BASE_URL, { placa: dados.placa, chassi: dados.chassi });
        const gravou = await salvarVeiculo(API_BASE_URL, dados);
        if (!gravou) {
          falhas++;
          atualizarItem(item.id, { status: 'erro', mensagem: 'Lido, mas não foi possível gravar no cadastro.' });
          continue;
        }
        if (existente) atualizados++; else cadastrados++;
        const faltando = [!lido.placa && 'placa', !lido.chassi && 'chassi', !lido.renavam && 'RENAVAM', !lido.marcaModelo && 'marca/modelo'].filter(Boolean);
        const suspeitos = duvidas.filter((c) => ['placa', 'chassi', 'renavam'].includes(c));
        atualizarItem(item.id, {
          status: faltando.length || suspeitos.length ? 'atencao' : 'ok',
          progresso: 100,
          placa: dados.placa,
          marcaModelo: dados.marcaModelo,
          mensagem: `${existente ? 'Cadastro atualizado' : 'Cadastrado'}`
            + `${faltando.length ? ` · não encontrei: ${faltando.join(', ')}` : ''}`
            + `${suspeitos.length ? ` · confira: ${suspeitos.join(', ')}` : ''}`,
        });
      } catch (error) {
        console.error(`Erro ao ler ${item.nome}:`, error);
        falhas++;
        atualizarItem(item.id, { status: 'erro', mensagem: 'Não foi possível ler este documento.' });
      }
    }

    setProcessando(false);
    carregar(0, buscaAplicada);
    const resumo = [cadastrados && `${cadastrados} cadastrado(s)`, atualizados && `${atualizados} atualizado(s)`, falhas && `${falhas} com falha`]
      .filter(Boolean).join(', ');
    addToast(falhas ? 'warning' : 'success', `Importação concluída${resumo ? `: ${resumo}` : ''}. ${AVISO_LEITURA}`);
  };

  const escolherArquivos = (ev) => {
    const arquivos = Array.from(ev.target.files || []);
    ev.target.value = ''; // permite escolher os mesmos arquivos de novo
    if (arquivos.length === 0) return;
    const itens = arquivos.map((arquivo, i) => {
      const base = { id: `${Date.now()}-${i}`, arquivo, nome: arquivo.name, progresso: 0, placa: '', marcaModelo: '' };
      if (!TIPOS_ACEITOS.includes(arquivo.type)) return { ...base, status: 'erro', mensagem: 'Formato não suportado (use PDF, JPG ou PNG).' };
      if (arquivo.size > TAMANHO_MAXIMO_LEITURA_MB * 1024 * 1024) return { ...base, status: 'erro', mensagem: `Arquivo acima de ${TAMANHO_MAXIMO_LEITURA_MB} MB.` };
      return { ...base, status: 'aguardando', mensagem: 'Aguardando' };
    });
    setFila(itens);
    processarFila(itens);
  };

  const resumoFila = {
    total: fila.length,
    concluidos: fila.filter((i) => ['ok', 'atencao', 'erro', 'cancelado'].includes(i.status)).length,
  };

  const iconeStatus = (status) => {
    if (status === 'ok') return <FaCheckCircle color="#27ae60" />;
    if (status === 'atencao') return <FaExclamationTriangle color="#e67e22" />;
    if (status === 'erro') return <FaTimesCircle color="#e74c3c" />;
    if (status === 'lendo' || status === 'gravando') return <FaSpinner className="spin" color="#3498db" />;
    if (status === 'cancelado') return <FaStop color="#95a5a6" />;
    return <FaHourglassHalf color="#95a5a6" />;
  };

  return (
    <PageBackground>
      <Header />
      <ContentAmplo>
        <TitleRow>
          <Title><FaCar size={22} /> Veículos</Title>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <SecondaryButton type="button" onClick={() => inputMassaRef.current && inputMassaRef.current.click()} disabled={processando}>
              <FaUpload /> Importar documentos (vários)
            </SecondaryButton>
            <PrimaryButton type="button" onClick={abrirNovo}>
              <FaPlus /> Novo Veículo
            </PrimaryButton>
          </div>
          <input
            ref={inputMassaRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,application/pdf"
            onChange={escolherArquivos}
            style={{ display: 'none' }}
          />
        </TitleRow>

        {/* ── Importação em massa ─────────────────────────────────────────── */}
        {fila.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle><FaUpload size={16} /> Importação de documentos ({resumoFila.concluidos} de {resumoFila.total})</CardTitle>
              {processando ? (
                <SecondaryButton type="button" onClick={() => { cancelarRef.current = true; }}>
                  <FaStop /> Parar após o atual
                </SecondaryButton>
              ) : (
                <SecondaryButton type="button" onClick={() => setFila([])}>
                  <FaTimes /> Fechar
                </SecondaryButton>
              )}
            </CardHeader>
            <HintText style={{ display: 'block', marginBottom: 10 }}>
              Os documentos são lidos um por um no seu computador; os arquivos não são enviados nem guardados, só os dados lidos.
              {' '}{AVISO_LEITURA}
            </HintText>
            <BarraTotal><div style={{ width: `${resumoFila.total ? (resumoFila.concluidos / resumoFila.total) * 100 : 0}%` }} /></BarraTotal>
            <ListaFila>
              {fila.map((item) => (
                <ItemFila key={item.id}>
                  <span className="icone">{iconeStatus(item.status)}</span>
                  <div className="info">
                    <strong>{item.placa ? `${formatarPlaca(item.placa)}${item.marcaModelo ? ` — ${item.marcaModelo}` : ''}` : item.nome}</strong>
                    <span>
                      {item.placa && <>{item.nome} · </>}
                      {item.mensagem}{item.status === 'lendo' ? ` ${item.progresso}%` : ''}
                    </span>
                    {item.status === 'lendo' && <BarraItem><div style={{ width: `${item.progresso}%` }} /></BarraItem>}
                  </div>
                </ItemFila>
              ))}
            </ListaFila>
          </Card>
        )}

        {/* ── Pesquisa e lista ────────────────────────────────────────────── */}
        <Card>
          <SearchRow>
            <Input
              value={busca}
              onChange={(ev) => setBusca(ev.target.value)}
              onKeyPress={(ev) => ev.key === 'Enter' && pesquisar()}
              placeholder="Pesquisar por placa, chassi, RENAVAM, motor ou marca/modelo"
            />
            <PrimaryButton type="button" onClick={pesquisar} disabled={carregando}>
              <FaSearch /> Pesquisar
            </PrimaryButton>
          </SearchRow>
          <HintText>{totalElementos} veículo(s){buscaAplicada ? ` para "${buscaAplicada}"` : ' cadastrados'}</HintText>

          {carregando ? (
            <EmptyState><FaSpinner className="spin" /> Carregando...</EmptyState>
          ) : veiculos.length === 0 ? (
            <EmptyState>Nenhum veículo encontrado.</EmptyState>
          ) : (
            <TabelaWrapper>
              <Tabela>
                <thead>
                  <tr>
                    <th>Placa</th>
                    <th>Chassi</th>
                    <th>RENAVAM</th>
                    <th>Motor</th>
                    <th>Marca/Modelo</th>
                    <th>Ano fab./mod.</th>
                    <th>Cor</th>
                    <th>Atualizado em</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {veiculos.map((v) => (
                    <tr key={v.idVeiculo}>
                      <td><strong>{formatarPlaca(v.placa) || '—'}</strong></td>
                      <td className="mono">{v.chassi || '—'}</td>
                      <td className="mono">{v.renavam || '—'}</td>
                      <td className="mono">{v.motor || '—'}</td>
                      <td className="quebra">{v.marcaModelo || '—'}</td>
                      <td>{v.anoFabricacaoModelo || '—'}</td>
                      <td>{v.cor || '—'}</td>
                      <td>{formatarData(v.dataAlteracao || v.dataCadastro)}</td>
                      <td>
                        <BotaoIcone type="button" onClick={() => abrirEdicao(v)} title="Ver / editar">
                          <FaEdit />
                        </BotaoIcone>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabela>
            </TabelaWrapper>
          )}

          {totalPaginas > 1 && (
            <Paginacao>
              <SecondaryButton type="button" onClick={() => carregar(pagina - 1, buscaAplicada)} disabled={pagina === 0 || carregando}>
                <FaChevronLeft /> Anterior
              </SecondaryButton>
              <span>Página {pagina + 1} de {totalPaginas}</span>
              <SecondaryButton type="button" onClick={() => carregar(pagina + 1, buscaAplicada)} disabled={pagina >= totalPaginas - 1 || carregando}>
                Próxima <FaChevronRight />
              </SecondaryButton>
            </Paginacao>
          )}
        </Card>
      </ContentAmplo>

      {/* ── Modal de cadastro / edição ──────────────────────────────────── */}
      {modalAberto && (
        <ModalOverlay>
          <ModalBox style={{ maxWidth: 720 }}>
            <ModalHeader>
              <h3>{form.idVeiculo ? 'Editar Veículo' : 'Novo Veículo'}</h3>
              <ModalClose onClick={() => setModalAberto(false)}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              <ImportarDocumentoVeiculo onDadosLidos={aplicarDocumento} gravar={false} disabled={salvando} />
              <FormRow>
                <InputGroup>
                  <Label>Placa</Label>
                  <Input value={form.placa} onChange={campo('placa', (v) => formatarPlaca(v).slice(0, 8))} placeholder="AAA-0A00" maxLength={8} hasError={!!erros.placa} />
                  {erros.placa && <ErrorText>{erros.placa}</ErrorText>}
                </InputGroup>
                <InputGroup style={{ flex: 2 }}>
                  <Label>Chassi</Label>
                  <Input value={form.chassi} onChange={campo('chassi', (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="17 caracteres" maxLength={20} hasError={!!erros.chassi} />
                  {erros.chassi && <ErrorText>{erros.chassi}</ErrorText>}
                </InputGroup>
              </FormRow>
              <FormRow>
                <InputGroup>
                  <Label>RENAVAM</Label>
                  <Input value={form.renavam} onChange={campo('renavam', (v) => v.replace(/\D/g, ''))} maxLength={11} hasError={!!erros.renavam} />
                  {erros.renavam && <ErrorText>{erros.renavam}</ErrorText>}
                </InputGroup>
                <InputGroup>
                  <Label>Ano fab./modelo</Label>
                  <Input value={form.anoFabricacaoModelo} onChange={campo('anoFabricacaoModelo')} placeholder="2015/2016" maxLength={9} />
                </InputGroup>
                <InputGroup>
                  <Label>Cor</Label>
                  <Input value={form.cor} onChange={campo('cor', (v) => v.toUpperCase())} maxLength={30} />
                </InputGroup>
              </FormRow>
              <FormRow>
                <InputGroup>
                  <Label>Nº do motor</Label>
                  <Input value={form.motor} onChange={campo('motor', (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="Número gravado no motor" maxLength={25} />
                </InputGroup>
                <InputGroup style={{ flex: 2 }}>
                  <Label>Marca/Modelo</Label>
                  <Input value={form.marcaModelo} onChange={campo('marcaModelo', (v) => v.toUpperCase())} placeholder="Ex.: VW/GOL 1.0" maxLength={100} />
                </InputGroup>
              </FormRow>
              {form.idVeiculo && <HintText>Na edição, o que for apagado aqui também é apagado do cadastro.</HintText>}
              {erros.geral && <ErrorText style={{ display: 'block', marginTop: 8 }}>{erros.geral}</ErrorText>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <SecondaryButton type="button" onClick={() => setModalAberto(false)}>Cancelar</SecondaryButton>
                <PrimaryButton type="button" onClick={salvar} disabled={salvando}>
                  <FaSave /> {salvando ? 'Salvando...' : 'Salvar'}
                </PrimaryButton>
              </div>
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}
    </PageBackground>
  );
}

// Tela mais larga que as de documento (900px): a tabela de veículos precisa de espaço para todas as colunas
const ContentAmplo = styled(Content)`
  max-width: 1600px;
  width: 100%;
  box-sizing: border-box;
`;

// A rolagem horizontal só aparece em telas estreitas (celular/tablet)
const TabelaWrapper = styled.div`
  overflow-x: auto;
  margin-top: 14px;
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th, td { padding: 12px 14px; border-bottom: 1px solid #eef1f1; text-align: left; white-space: nowrap; }
  td.quebra { white-space: normal; min-width: 220px; }
  th { background: #f4f6f6; color: #555; font-weight: 600; font-size: 12.5px; text-transform: uppercase; }
  td.mono { font-family: Consolas, 'Courier New', monospace; font-size: 13.5px; }
  tbody tr:hover { background: #f9fbfb; }
`;

const BotaoIcone = styled.button`
  background: none;
  border: 1px solid #008080;
  color: #008080;
  border-radius: 6px;
  padding: 5px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover { background: #e6f7f5; }
`;

const Paginacao = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
  font-size: 13px;
  color: #555;
`;

const BarraTotal = styled.div`
  height: 8px;
  border-radius: 4px;
  background: #e3e6ea;
  overflow: hidden;
  margin-bottom: 12px;
  div { height: 100%; background: #008080; transition: width 0.3s ease; }
`;

const BarraItem = styled.div`
  height: 6px;
  border-radius: 3px;
  background: #e3e6ea;
  overflow: hidden;
  margin-top: 4px;
  div { height: 100%; background: #6f42c1; transition: width 0.3s ease; }
`;

const ListaFila = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 65vh;
  overflow-y: auto;
`;

const ItemFila = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #eef1f1;
  border-radius: 8px;
  .icone { margin-top: 2px; }
  .info { display: flex; flex-direction: column; flex: 1; min-width: 0; }
  strong { font-size: 13px; color: #2c3e50; }
  span { font-size: 12px; color: #7f8c8d; }
`;

export default Veiculos;
